import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

export const SUPPORTED_LANGUAGES = ['en', 'de', 'pt-PT'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly defaultLanguage: SupportedLanguage = 'en';
  private readonly storageKey = 'kore_language';
  private readonly isBrowser: boolean;

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.translate.addLangs([...SUPPORTED_LANGUAGES]);
  }

  initialize(): Observable<unknown> {
    return this.useLanguage(this.resolveInitialLanguage());
  }

  useLanguage(language: string | null | undefined): Observable<unknown> {
    const normalizedLanguage = this.normalizeLanguage(language);

    this.document.documentElement.lang = normalizedLanguage;

    if (this.isBrowser) {
      localStorage.setItem(this.storageKey, normalizedLanguage);
    }

    return this.translate.use(normalizedLanguage);
  }

  getCurrentLanguage(): SupportedLanguage {
    return this.normalizeLanguage(this.translate.getCurrentLang() || this.defaultLanguage);
  }

  private resolveInitialLanguage(): SupportedLanguage {
    if (!this.isBrowser) {
      return this.defaultLanguage;
    }

    return this.normalizeLanguage(
      this.getStoredUserLanguage() ||
      localStorage.getItem(this.storageKey) ||
      navigator.language
    );
  }

  private getStoredUserLanguage(): string | null {
    const storedUser = localStorage.getItem('currentUser');

    if (!storedUser) {
      return null;
    }

    try {
      const user = JSON.parse(storedUser) as { language?: string };
      return user.language || null;
    } catch {
      return null;
    }
  }

  normalizeLanguage(language: string | null | undefined): SupportedLanguage {
    if (!language) {
      return this.defaultLanguage;
    }

    const normalizedLanguage = language.trim().toLowerCase();

    if (normalizedLanguage === 'pt' || normalizedLanguage === 'pt-pt') {
      return 'pt-PT';
    }

    if (normalizedLanguage === 'de' || normalizedLanguage === 'de-de') {
      return 'de';
    }

    if (normalizedLanguage === 'en' || normalizedLanguage.startsWith('en-')) {
      return 'en';
    }

    return this.defaultLanguage;
  }
}
